import React from 'react';

import cs from 'classnames';
import {withRouter, WithRouterProps} from 'react-router';

import {DashboardSidebarFoldersList} from 'app/components/general/DashboardPage/DashboardSidebarFoldersList';
import MainCol from 'app/components/general/MainCol';
import {StyledSidebarDIV} from 'app/components/general/Sidebar';
import {DashboardItemsCounter} from 'app/general/dashboards/DashboardItemsCounter';
import {BootstrapMinMediumMediaQuery} from 'app/general/mediaqueries';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {DocumentsNotificationsProvider} from 'app/notifly/components/DocumentsNotificationsProvider';
import {PresenceSubscriber} from 'app/presence/components/PresenceSubscriber';
import {useSlimTasks} from 'app/tasks/api';
import {useBulkTasksSelect} from 'app/tasks/components/TasksDashboard/BulkSelectTasksContext';
import {TaskBulkActionsButton} from 'app/tasks/components/TasksDashboard/TaskBulkActionsButton';
import {useAllTasksDashboardFilters} from 'app/tasks/dashboard-utils';
import {setLastUsedTasksDashboard} from 'app/tasks/slice';
import {gt, ngt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import {CompactTasksList} from './CompactTasksList';
import {TasksFilterbar} from './TasksFilterbar';
import {TasksLinkPagination} from './TasksLinkPagination';

import type {TaskDashboardLocationParamsType} from 'app/tasks/types';

interface Props
  extends WithRouterProps<
    Record<string, unknown>,
    TaskDashboardLocationParamsType
  > {
  isManager: boolean;
  userTimezone: string;
  toggleAssignTaskModal: () => void;
}

export const AllTasksList = withRouter(
  ({
    isManager,
    toggleAssignTaskModal,
    location,
    userTimezone,
    router,
  }: Props): JSX.Element | null => {
    const actions = useAsyncActions({
      setLastUsedDashboard: setLastUsedTasksDashboard,
    });

    const {redirecting, fetchParams, viewingAll} = useAllTasksDashboardFilters(
      location,
      router
    );

    let {currentData} = useSlimTasks(fetchParams, {
      skip: redirecting,
    });

    const {
      selectedTasks,
      isIndeterminateState,
      handleSelectedTasks,
      enableBulkSelect,
      setTasksCount,
      setSelectedTasks,
    } = useBulkTasksSelect();

    React.useEffect(() => {
      actions.setLastUsedDashboard('all');
    }, []);

    React.useEffect(() => {
      if (currentData == null) {
        setSelectedTasks([]);
        return;
      }
      setSelectedTasks(
        currentData.results.filter((selectedTask) =>
          selectedTasks.some((task) => task.id === selectedTask.id)
        )
      );
      setTasksCount(currentData.results.length);
    }, [currentData]);

    if (redirecting || !currentData) {
      return null;
    }

    const tasks = currentData!;

    function resetFilters() {
      router.replace({pathname: location.pathname});
    }

    function getTasksList() {
      const results: Array<number> = [];
      tasks.results.map((p) => {
        if (p.id != null) {
          results.push(p.id);
        }
      });
      return results;
    }

    return (
      <div className="row">
        <BootstrapMinMediumMediaQuery>
          <StyledSidebarDIV>
            <DashboardSidebarFoldersList />
          </StyledSidebarDIV>
        </BootstrapMinMediumMediaQuery>
        <MainCol>
          <TasksFilterbar
            resetFilters={resetFilters}
            viewingAll={viewingAll}
            userTimezone={userTimezone}
          />
          <div className="row">
            <div className="col-xl-1 col-md-2 align-items-center d-sm-none d-md-flex">
              <small className="text-muted">{gt('Date')}</small>
            </div>
            <div className="d-flex col">
              <div className="row pl-sm-2">
                <CheckboxButton
                  isChecked={
                    selectedTasks.length === tasks.results.length ||
                    isIndeterminateState
                  }
                  onClick={(isChecked) =>
                    handleSelectedTasks(isChecked, tasks.results)
                  }
                  useRealCheckbox={true}
                  isIndeterminate={isIndeterminateState}
                  className={cs('mr-3 btn btn-sm btn-link', {
                    'd-none': !enableBulkSelect || tasks.count === 0,
                  })}
                />
                <DashboardItemsCounter
                  text={ngt('%s task', '%s tasks', tasks.count)}
                  showSearchHelp={!!location.query.search}
                />
                <span
                  className={cs('my-2 small ml-1 text-muted', {
                    'd-none': !enableBulkSelect || selectedTasks.length === 0,
                  })}
                >
                  {gt('(%s selected)', selectedTasks.length)}
                </span>
              </div>
            </div>
            <div className="col-1 align-self-end mb-1">
              <TaskBulkActionsButton
                enableBulkSelect={enableBulkSelect}
                tasks={selectedTasks}
              />
            </div>
          </div>
          <DocumentsNotificationsProvider
            contentType="taskinstance"
            documentsIds={getTasksList()}
          >
            {(tasksNotifications) => (
              <CompactTasksList
                tasks={tasks}
                notifications={tasksNotifications}
                canCreate={isManager}
                showAssignTaskModal={toggleAssignTaskModal}
                userTimezone={userTimezone}
              />
            )}
          </DocumentsNotificationsProvider>
          <PresenceSubscriber
            contentType="taskinstance"
            objectsIds={tasks.results.map((p) => p.id).filter((id) => id) ?? []}
            stealth={true}
          />
          <TasksLinkPagination
            params={location.query}
            userTimezone={userTimezone}
            response={tasks}
          />
        </MainCol>
      </div>
    );
  }
);
